/**
 * ChatThread — the conversation transcript renderer, extracted verbatim from
 * AgentPage.jsx (Phase 2 chat-core split). One implementation renders the
 * thread everywhere: the standalone AI Agent page today, an embedded copilot
 * dock tomorrow (`compact` tightens spacing for narrow hosts).
 *
 * All side effects arrive as handler props — the thread itself owns no
 * transport, no routing and no toasts (the "presentational core, host shell"
 * split).
 */
import { useMemo, useState } from 'react';
import { marked } from 'marked';
import {
  FiCopy, FiEdit2, FiChevronRight, FiChevronLeft, FiSearch, FiAlertCircle, FiGitBranch, FiThumbsUp, FiThumbsDown, FiDownload,
} from 'react-icons/fi';
import sanitizeDocHtml from '../utils/sanitizeHtml';
import { AttachmentChip } from './attachments';
import {
  ThinkingDots, ArtifactCard, ProposalCard, DetailsConfirmCard, FactsFormCard, MsgAction, WebSourceLink,
  SpreadsheetEditCard, SOURCE_ROUTES,
} from './cards';

marked.setOptions({ gfm: true, breaks: true });

/**
 * Strip anchors that do not point at the web, keeping their text.
 *
 * A model writing "Download: [file.xlsx](edit-mtiq0oc4-ua0zkv)" produces a
 * RELATIVE link. The browser resolves it against the app's own origin, the
 * router has no such route, and the user is bounced to the dashboard — having
 * clicked what looked like their file. Nothing the model can write is a valid
 * in-app address, so no such anchor should ever be clickable: real files reach
 * the user as cards (documents, spreadsheet downloads), never as prose links.
 *
 * Runs AFTER the sanitizer, on already-clean HTML, and unwraps rather than
 * deletes — the user still reads the filename, it just cannot take them away.
 */
const webLinksOnly = (html) => {
  if (!html.includes('<a')) return html;
  const tpl = document.createElement('template');
  tpl.innerHTML = html;
  tpl.content.querySelectorAll('a[href]').forEach((a) => {
    if (!/^(?:https?:|mailto:)/i.test(a.getAttribute('href') || '')) {
      a.replaceWith(...a.childNodes);
    }
  });
  return tpl.innerHTML;
};

export function Message({ msg, index, onCopy, onOpenArtifact, activeArtifact, onConfirmProposal, onEditResend, onSwitchVersion, onBranchFrom, onNavigateSource, onOpenAttachment, onFeedback, onExport, canEdit, inlineDocKind }) {
  const isUser = msg.role === 'user';
  const [editing, setEditing] = useState(false);
  const [draftText, setDraftText] = useState('');
  // Thumbs-down opens a one-tap reason row before the feedback is sent.
  const [pendingDown, setPendingDown] = useState(false);
  // Markdown parse + sanitize is the expensive part of a chat render, and
  // streaming re-renders the WHOLE transcript on every chunk. Memoized per
  // message, only the streaming bubble re-parses — the rest return cached HTML.
  // The studio's own document is on screen; only OTHER documents need a card.
  const shownDrafts = useMemo(
    () => (msg.drafts || []).filter((d) => !inlineDocKind || d.kind !== inlineDocKind),
    [msg.drafts, inlineDocKind],
  );
  const renderedHtml = useMemo(
    () => webLinksOnly(sanitizeDocHtml(marked.parse(msg.content || '', { breaks: true }))),
    [msg.content],
  );

  if (isUser) {
    const startEdit = () => { setDraftText(msg.content); setEditing(true); };
    const submitEdit = () => {
      const next = draftText.trim();
      setEditing(false);
      if (!next || next === msg.content) return;
      onEditResend?.(index, next);
    };
    const versionCount = msg.versions?.length || 0;
    const curVersion = msg.current ?? versionCount - 1;

    return (
      <div className="group flex flex-col items-end gap-1.5">
        {/* items-end matters twice: without an explicit alignment a file chip
            next to an inline image stretches to that image's height (a giant
            empty card), and `end` sits the chips on the image's bottom edge so
            the whole group shares a baseline with the bubble below. */}
        {msg.attachments && msg.attachments.length > 0 && (
          <div className="flex flex-wrap items-end justify-end gap-2 max-w-[85%]">
            {msg.attachments.map((a) => (
              a.fileType === 'image' && a.previewUrl ? (
                // ChatGPT/Claude-style inline image — click opens the lightbox.
                <button
                  key={a.id}
                  type="button"
                  onClick={() => onOpenAttachment?.(msg, a)}
                  title={a.name}
                  className="rounded-xl overflow-hidden border transition-all hover:shadow-[0_8px_22px_-12px_rgba(8,17,31,0.4)] hover:-translate-y-0.5 active:scale-[0.98]"
                  style={{ borderColor: '#e2d9c8' }}
                  aria-label={`View ${a.name}`}
                >
                  <img src={a.previewUrl} alt={a.name} className="block max-h-40 max-w-[240px] object-cover" />
                </button>
              ) : (
                // Files open the same lightbox as images (extracted text view).
                // Static chip on hosts that don't render a preview overlay.
                <AttachmentChip
                  key={a.id}
                  att={a}
                  onOpen={onOpenAttachment ? (att) => onOpenAttachment(msg, att) : undefined}
                />
              )
            ))}
          </div>
        )}
        {/* User text renders as markdown too (breaks preserved) so quoted
            draft passages, lists and emphasis look intentional, not raw. */}
        {editing ? (
          <div className="w-full max-w-[85%] rounded-2xl border p-2" style={{ borderColor: '#145f5f', background: '#fff' }}>
            <textarea
              value={draftText}
              onChange={(e) => setDraftText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); submitEdit(); }
                if (e.key === 'Escape') setEditing(false);
              }}
              autoFocus
              rows={Math.min(8, Math.max(2, draftText.split('\n').length))}
              className="w-full resize-none outline-none text-[14px] leading-relaxed text-[#08111f] px-2 py-1"
            />
            <div className="flex items-center justify-end gap-2 px-1 pb-0.5">
              <button onClick={() => setEditing(false)} className="text-[11.5px] font-semibold text-[#7a7060] hover:text-[#08111f] px-2 py-1">Cancel</button>
              <button onClick={submitEdit} className="text-[11.5px] font-semibold text-white px-3 py-1 rounded-lg" style={{ background: '#145f5f' }}>
                Send
              </button>
            </div>
            <div className="text-[10px] text-[#9a9080] px-2 pb-1">Sending replaces the answers after this message.</div>
          </div>
        ) : (
          <div
            className="chat-md max-w-[85%] px-4 py-2.5 rounded-2xl rounded-br-md text-[14px] leading-relaxed text-[#08111f] break-words"
            style={{ background: '#ece6d8' }}
            dangerouslySetInnerHTML={{ __html: renderedHtml }}
          />
        )}
        {!editing && (
          <div className="flex items-center gap-0.5 pr-0.5">
            {versionCount > 1 && onSwitchVersion && (
              <span className="inline-flex items-center gap-0.5 mr-1 text-[11px] text-[#9a9080]" title="This message was edited — flip between versions">
                <MsgAction title="Previous version" disabled={curVersion <= 0 || !canEdit} onClick={() => onSwitchVersion(index, curVersion - 1)}>
                  <FiChevronLeft size={14} />
                </MsgAction>
                <span className="tabular-nums select-none">{curVersion + 1}/{versionCount}</span>
                <MsgAction title="Next version" disabled={curVersion >= versionCount - 1 || !canEdit} onClick={() => onSwitchVersion(index, curVersion + 1)}>
                  <FiChevronRight size={14} />
                </MsgAction>
              </span>
            )}
            <MsgAction title="Copy message" onClick={() => onCopy(msg.content)}><FiCopy size={13} /></MsgAction>
            {onEditResend && (
              <MsgAction title="Edit & resend" disabled={!canEdit} onClick={startEdit}><FiEdit2 size={13} /></MsgAction>
            )}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="group flex gap-3">
      <div className="w-7 h-7 rounded-lg shrink-0 overflow-hidden mt-0.5" style={{ boxShadow: '0 2px 6px -2px rgba(13,74,69,0.4)' }}>
        <img src="/vee-mascot.svg" alt="Vee" className="w-full h-full object-contain" />
      </div>
      <div className="min-w-0 flex-1">
        {msg.recoverFailed ? (
          // Reload-recovery gave up (server still finishing, or briefly offline).
          <div className="text-[13px] leading-relaxed text-[#7a7060] flex items-center gap-2">
            <FiAlertCircle size={14} className="text-[#9a7a3a] shrink-0" />
            This answer is taking longer than expected — reopen this chat from History in a moment to see it.
          </div>
        ) : msg.recovering ? (
          // Reloaded mid-generation — the answer is being recovered from the server.
          <div className="flex items-center gap-2 text-[13px] text-[#7a7060]">
            <ThinkingDots /> <span>Reconnecting to your answer…</span>
          </div>
        ) : (!msg.content && msg.loading) ? (
          // A drafting turn spends most of its time inside a tool, before a
          // single token exists. The server says what it is doing — show it.
          msg.status ? (
            <div className="flex items-center gap-2 text-[13px] text-[#7a7060]">
              <ThinkingDots /> <span>{msg.status}</span>
            </div>
          ) : <ThinkingDots />
        ) : msg.loading ? (
          // While streaming, show raw text (whitespace preserved). Parsing markdown
          // on every token would re-parse the whole growing string each frame (O(N²)).
          <div className="text-[14px] leading-relaxed text-[#1a2230]" style={{ whiteSpace: 'pre-wrap' }}>
            {msg.content}
            <span className="inline-block w-[7px] h-[15px] ml-0.5 align-text-bottom animate-pulse" style={{ background: '#145f5f' }} />
          </div>
        ) : (
          <div className="chat-md text-[14px] leading-relaxed text-[#1a2230]" dangerouslySetInnerHTML={{ __html: renderedHtml }} />
        )}

        {!msg.loading && msg.content && ((msg.sources && msg.sources.length > 0) || msg.hasEstimate) && (
          <div className="mt-2.5 flex flex-wrap items-center gap-1.5">
            {msg.hasEstimate && (
              <span
                className="inline-flex items-center gap-1 h-6 px-2 rounded-full text-[9px] font-semibold uppercase tracking-[0.5px] text-[#9a7a3a] bg-[rgba(176,137,58,0.1)] border border-[rgba(176,137,58,0.25)]"
                title="Figures are computed estimates — verify on the MCA/IT portal before filing."
              >
                <FiAlertCircle size={11} /> Estimate
              </span>
            )}
            {msg.sources && msg.sources.length > 0 && (
              <span
                className="inline-flex items-center gap-1.5 h-6 px-2 rounded-full text-[10px] tracking-[0.3px] bg-[rgba(20,95,95,0.06)] border border-[#e2d9c8]"
                title="Answered from your live data, not the model's memory. Click a source to open its page."
              >
                <FiSearch size={11} className="text-[#145f5f]" />
                <span className="uppercase font-semibold text-[9px] tracking-[0.5px] text-[#9a9080]">Sources</span>
                {msg.sources.map((label, si) => (
                  <span key={label} className="inline-flex items-center">
                    {si > 0 && <span className="text-[#c9c0ab] mx-0.5">·</span>}
                    {SOURCE_ROUTES[label] && onNavigateSource ? (
                      <button
                        type="button"
                        onClick={() => onNavigateSource(SOURCE_ROUTES[label])}
                        className="inline-flex items-center gap-0.5 text-[#145f5f] underline decoration-dotted decoration-[#145f5f]/40 underline-offset-2 hover:decoration-solid"
                        title={`Open ${label}`}
                      >
                        {label}
                        <FiChevronRight size={9} className="shrink-0" />
                      </button>
                    ) : (
                      <span className="text-[#5a5347]">{label}</span>
                    )}
                  </span>
                ))}
              </span>
            )}
          </div>
        )}

        {/* Web-search citations — clickable, external sites need a second
            confirming click (minor warning before leaving the app). */}
        {!msg.loading && msg.webSources && msg.webSources.length > 0 && (
          <div className="mt-2 flex flex-wrap items-center gap-1.5">
            <span className="text-[9px] uppercase tracking-[0.5px] font-semibold text-[#9a9080]">Web sources</span>
            {msg.webSources.map((s) => <WebSourceLink key={s.url} source={s} />)}
            <span className="basis-full text-[9.5px] text-[#9a7a3a] mt-0.5">
              Web results can be outdated or wrong — verify before relying on them.
            </span>
          </div>
        )}

{/* In a studio, the document is ALREADY open beside the chat, so a card
            offering to "Open" it points at what the user is looking at. Drafts
            of the studio's own kind are dropped here; a draft of any OTHER kind
            (a resolution written while the minutes are open) is not the document
            beside them and still gets its card. */}
        {shownDrafts.length > 0 && (
          <div className="mt-3 flex flex-col gap-2">
            {shownDrafts.map((d, di) => <ArtifactCard key={di} draft={d} onOpen={onOpenArtifact} active={activeArtifact === d} />)}
          </div>
        )}
        {msg.spreadsheetEdits && msg.spreadsheetEdits.length > 0 && (
          <div className="mt-3 flex flex-col gap-2">
            {msg.spreadsheetEdits.map((e) => <SpreadsheetEditCard key={e.attachmentId} edit={e} />)}
          </div>
        )}
        {msg.proposals && msg.proposals.length > 0 && (
          <div className="mt-3 flex flex-col gap-2.5">
            {msg.proposals.map((p, pi) => <ProposalCard key={pi} proposal={p} onConfirm={onConfirmProposal} />)}
          </div>
        )}
        {/* Completed detail-confirms show a compact receipt; PENDING ones dock
            above the composer with the facts forms (one connected panel). */}
        {msg.detailsConfirms && msg.detailsConfirms.some((c) => c.confirmed) && (
          <div className="mt-3 flex flex-col gap-2.5">
            {msg.detailsConfirms.filter((c) => c.confirmed).map((c, ci) => (
              <DetailsConfirmCard key={ci} card={c} />
            ))}
          </div>
        )}
        {/* Completed ask-forms show a compact receipt in the transcript.
            PENDING forms render docked above the composer (Claude-style) —
            see pendingAsk in the host page. */}
        {msg.factsRequests && msg.factsRequests.some((c) => c.completed) && (
          <div className="mt-3 flex flex-col gap-2.5">
            {msg.factsRequests.filter((c) => c.completed).map((c, ci) => (
              <FactsFormCard key={ci} card={c} />
            ))}
          </div>
        )}

        {!msg.loading && msg.content && (
          <div className="mt-1.5 -ml-1.5 flex items-center gap-0.5">
            <MsgAction title="Copy message" onClick={() => onCopy(msg.content)}><FiCopy size={13} /></MsgAction>
            {onBranchFrom && index > 0 && (
              <MsgAction title="Branch to a new chat from here" disabled={!canEdit} onClick={() => onBranchFrom(index)}>
                <FiGitBranch size={13} />
              </MsgAction>
            )}
            {/* Take this answer away as a file, with its as-on date and sources set out. */}
            {onExport && !msg.seeded && !msg.failed && msg.content && msg.content.trim().length > 40 && (
              <>
                <MsgAction title="Download as Word, with the as-on date and sources" onClick={() => onExport(index, 'docx')}>
                  <FiDownload size={13} /><span className="ml-0.5 text-[10px]">Word</span>
                </MsgAction>
                <MsgAction title="Download as PDF, with the as-on date and sources" onClick={() => onExport(index, 'pdf')}>
                  <FiDownload size={13} /><span className="ml-0.5 text-[10px]">PDF</span>
                </MsgAction>
              </>
            )}
            {/* Thumbs feedback (D-3): explicit, per-click volunteered — the
                tooltip discloses exactly what gets sent. */}
            {onFeedback && !msg.seeded && !msg.failed && (msg.feedback ? (
              <span className="text-[10px] px-1.5 text-[#9a9080]">Thanks — noted</span>
            ) : pendingDown ? (
              <span className="flex items-center gap-1 flex-wrap pl-1">
                {[['wrong_facts', 'Wrong facts'], ['bad_formatting', 'Bad formatting'], ['did_not_listen', 'Didn’t listen']].map(([k, l]) => (
                  <button
                    key={k}
                    type="button"
                    onClick={() => { setPendingDown(false); onFeedback(index, 'down', [k]); }}
                    className="text-[10px] font-semibold px-2 py-[3px] rounded-full border transition-colors hover:bg-[#f6f3ea]"
                    style={{ borderColor: '#ddd6c6', color: '#5a5145' }}
                  >
                    {l}
                  </button>
                ))}
                <button
                  type="button"
                  onClick={() => { setPendingDown(false); onFeedback(index, 'down', []); }}
                  className="text-[10px] px-1.5 text-[#9a9080] hover:underline"
                >
                  Skip
                </button>
              </span>
            ) : (
              <>
                <MsgAction title="Good reply — sends this exchange (identifiers removed) to improve Vee" onClick={() => onFeedback(index, 'up', [])}>
                  <FiThumbsUp size={12.5} />
                </MsgAction>
                <MsgAction title="Bad reply — sends this exchange (identifiers removed) to improve Vee" onClick={() => setPendingDown(true)}>
                  <FiThumbsDown size={12.5} />
                </MsgAction>
              </>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * The transcript: one Message per turn plus the scroll anchor. `compact`
 * tightens width/spacing for embedded hosts (the copilot dock); the standalone
 * page passes nothing and gets today's exact layout.
 */
export default function ChatThread({
  messages,
  compact = false,
  endRef,
  onCopy,
  onOpenArtifact,
  activeArtifact,
  onConfirmProposal,
  onFeedback,
  onExport,
  onEditResend,
  onSwitchVersion,
  onBranchFrom,
  onNavigateSource,
  onOpenAttachment,
  canEdit,
  // When this thread sits beside a studio document ('minutes' | 'report'),
  // drafts of that kind render no card — the document is already visible.
  inlineDocKind = null,
}) {
  return (
    <div
      className={compact ? 'px-3 py-4 flex flex-col gap-5' : 'max-w-[760px] mx-auto px-4 py-6 flex flex-col gap-7'}
      style={{ fontFamily: "'Outfit', sans-serif" }}
    >
      {messages.map((msg, i) => (
        <Message
          key={i}
          msg={msg}
          index={i}
          onCopy={onCopy}
          onOpenArtifact={onOpenArtifact}
          activeArtifact={activeArtifact}
          onConfirmProposal={onConfirmProposal}
          onFeedback={onFeedback}
          onExport={onExport}
          onEditResend={onEditResend}
          onSwitchVersion={onSwitchVersion}
          onBranchFrom={onBranchFrom}
          onNavigateSource={onNavigateSource}
          onOpenAttachment={onOpenAttachment}
          canEdit={canEdit}
          inlineDocKind={inlineDocKind}
        />
      ))}
      {endRef ? <div ref={endRef} /> : null}
    </div>
  );
}
